import { type Ref, computed, ref } from 'vue';

import { useMediaQuery } from '@vueuse/core';

import {
	canUseDesktopSplitView,
	desktopPointerInteractionQuery,
	isNativeCapacitorPlatform,
} from 'src/util/platform/browserInteractionCapabilities';

const BREAKPOINTS = {
	lg: 1024,
	md: 768,
	sm: 640,
	xl: 1280,
	xs: 480,
} as const;

interface BreakpointState {
	desktopSplitViewSupported: Readonly<Ref<boolean>>;
	isDesktop: Readonly<Ref<boolean>>;
	isMobile: Readonly<Ref<boolean>>;
	isTablet: Readonly<Ref<boolean>>;
	isXs: Readonly<Ref<boolean>>;
	width: Readonly<Ref<number>>;
}

const sharedWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
let isViewportListenerAttached = false;

const refreshSharedWidth = (): void => {
	if (typeof window !== 'undefined') {
		sharedWidth.value = window.innerWidth;
	}
};

const attachViewportListenerOnce = (): void => {
	if (isViewportListenerAttached || typeof window === 'undefined') {
		return;
	}

	isViewportListenerAttached = true;
	window.addEventListener('resize', refreshSharedWidth, { passive: true });
};

export function useBreakpoint(): BreakpointState {
	refreshSharedWidth();
	attachViewportListenerOnce();

	const desktopPointerInteractionSupported = useMediaQuery(desktopPointerInteractionQuery);

	const isDesktop = computed(() => sharedWidth.value >= BREAKPOINTS.lg);
	const desktopSplitViewSupported = computed((): boolean =>
		canUseDesktopSplitView(isDesktop.value, {
			isNativeCapacitorPlatform: isNativeCapacitorPlatform(),
			matchMedia: () => ({ matches: desktopPointerInteractionSupported.value }),
		})
	);

	return {
		desktopSplitViewSupported,
		isDesktop,
		isMobile: computed(() => sharedWidth.value < BREAKPOINTS.lg),
		isTablet: computed(() => sharedWidth.value >= BREAKPOINTS.md && sharedWidth.value < BREAKPOINTS.lg),
		isXs: computed(() => sharedWidth.value < BREAKPOINTS.xs),
		width: sharedWidth as Readonly<Ref<number>>,
	};
}
